/**
 * topology.ts — data mirror of `plans/dashboard-rewrite/references/topology.md §1`.
 *
 * Single source of truth the shell Sidebar + SettingsNav consume.
 * Updating the product's top-level nav or the Settings section tree
 * is a one-file change (here), with a required sync to
 * `references/topology.md` first.
 *
 * Do NOT add:
 *   - a top-level Audits entry (reached via Sites or Settings →
 *     Developer → Logs);
 *   - shortcut rows for Docs / API keys / Webhooks / Logs / Members
 *     / Billing / Profile / Security / Integrations / Danger
 *     (Settings or `/docs/*` only — the 2026-05-27 trim removed the
 *     Developer-shortcut group from the primary sidebar).
 */

import { Logo } from '@/components/shell/Logo';
import type { Icon } from '@phosphor-icons/react';
import {
  Broadcast,
  Browsers,
  Buildings,
  ChartBar,
  CreditCard,
  Devices,
  File,
  Gauge,
  GearSix,
  GitBranch,
  GoogleChromeLogo,
  Key,
  ListBullets,
  PlugsConnected,
  Pulse,
  Question,
  Ranking,
  RocketLaunch,
  ShieldCheck,
  Sliders,
  TreeStructure,
  User,
  UserCirclePlus,
  Users,
  UsersThree,
  Wallet,
  Warning,
  WebhooksLogo,
  Wrench,
} from '@phosphor-icons/react';

// -----------------------------------------------------------------
// Types
// -----------------------------------------------------------------

export type PhosphorIcon = Icon;

export type NavChild = {
  /** Visible label. */
  label: string;
  /** Canonical destination. Must exist in routeTree.gen.ts. */
  href: string;
  /** Optional icon; Origins children omit this per shell-spec §2.4. */
  icon?: PhosphorIcon;
};

export type NavEntry = {
  label: string;
  href: string;
  icon: PhosphorIcon;
  /**
   * Static children (e.g. Docs pages). Dynamic children (Origins
   * recent-5) are resolved at render time via a resolver in the
   * Sidebar — this array stays undefined for those.
   *
   * A non-empty array makes the entry a *section*: the sidebar shows
   * the SidebarSubmenu panel for it (see `findNavSection`).
   */
  children?: NavChild[];
};

export type NavGroup = {
  /** Uppercase group header. `undefined` = render with no header. */
  header?: string;
  items: NavEntry[];
};

export type TailEntry = NavEntry;

export type SettingsSection = {
  /** Uppercase header for the left-pane group. */
  header: string;
  items: NavChild[];
};

export type Topology = {
  /** Primary nav groups (sidebar body, top→bottom). */
  primary: NavGroup[];
  /** Tail rows that sit just above the user pill (Settings, Help). */
  tail: TailEntry[];
  /** Floating "Invite" action — opens the Invite dialog anywhere. */
  action: {
    label: string;
    icon: PhosphorIcon;
    /** Route where the action dispatches (members + auto-open dialog). */
    actionHref: string;
  };
  /** Settings left-pane sections (rendered by SettingsNav). */
  settings: SettingsSection[];
};

// -----------------------------------------------------------------
// Data (mirror of references/topology.md)
// -----------------------------------------------------------------

export const topology: Topology = {
  primary: [
    {
      // Top-level lenses on the active site. Behaviours, Conversions, and
      // Spaces are disabled for now (unfinished surfaces) — removed from
      // the nav here AND their routes redirect to /performance.
      //   Performance — RUM CWV + synthetic Lighthouse (Audits child).
      //                 The brand's reason-for-being, so it sits first
      //                 and is the default landing surface.
      //   Traffic     — Visitors / sessions / sources / geo / devices,
      //                 plus a Live child for the realtime view.
      //
      // Site switching lives in the SidebarOrgPill popover above the
      // nav. Docs / API keys / Webhooks / Logs all live under
      // Settings → Developer / Help.
      items: [
        {
          label: 'Performance',
          href: '/performance',
          icon: Gauge,
          // The three data planes were promoted from `/performance`'s
          // `?view=` tab bar to child routes (2026-07-26) so the
          // section panel lists them; the entry's own href is the
          // cross-plane Overview (the panel's auto-prepended row).
          children: [
            { label: 'Real users', href: '/performance/real', icon: Pulse },
            { label: 'Field data', href: '/performance/field', icon: GoogleChromeLogo },
            { label: 'Audits', href: '/performance/audits', icon: ChartBar },
            // Promoted out of the Audits sub-tabs: it reads the same
            // runs but answers "which vendors cost me", and none of the
            // Audits view's scope / device / region filters apply to it.
            { label: 'Third parties', href: '/performance/third-parties', icon: PlugsConnected },
            // Segments reports Core Web Vitals per audience, which is this
            // section's question — Traffic's is who came and how many.
            // Both `/traffic/profiles` and `/performance/profiles` redirect
            // here: it shipped in the Traffic nav first, then under
            // Performance as "Profiles" before the rename.
            { label: 'Segments', href: '/performance/segments', icon: Devices },
            { label: 'Variants', href: '/performance/variants', icon: GitBranch },
          ],
        },
        {
          // SEO — the Lighthouse SEO score trend (the entry's own href
          // is the Overview, auto-prepended in the panel) + sitemap
          // health child (plans/seo quick slices 1–2). The GSC Search
          // lens joins here once its OAuth verification clears.
          label: 'SEO',
          href: '/seo',
          icon: Ranking,
          children: [{ label: 'Sitemap', href: '/seo/sitemap', icon: TreeStructure }],
        },
        {
          label: 'Traffic',
          href: '/traffic',
          icon: UsersThree,
          children: [
            { label: 'Live', href: '/traffic/live', icon: Broadcast },
            // Search (the Search Console lens) is out of the nav for now.
            // Its route still resolves, so a direct URL works for testing —
            // same treatment Integrations got in #507 while the Google
            // OAuth client and worker secrets are still ops-gated.
          ],
        },
      ],
    },
  ],
  tail: [
    // Staff-only. Present in the data for everyone, but the Sidebar filters
    // it out unless `is_system_admin` (GET /v1/user). The route is
    // independently gated server-side by `requireSystemAdmin`.
    {
      label: 'Admin',
      href: '/admin',
      icon: Wrench,
      // Staff-only children. `findNavSection` scans `tail` as well as
      // `primary`, so the section panel opens across `/admin/*` the same
      // way it does for Traffic and Performance. SidebarSubmenu re-applies
      // the `is_system_admin` filter the rail uses, so a non-staff user
      // who lands on /admin never sees the page list.
      children: [
        { label: 'Finances', href: '/admin/finances', icon: Wallet },
        { label: 'Ambient analysis', href: '/admin/analysis', icon: Pulse },
      ],
    },
    { label: 'Settings', href: '/settings', icon: GearSix },
    { label: 'Help', href: '/help', icon: Question },
  ],
  action: {
    label: 'Invite',
    icon: UserCirclePlus,
    actionHref: '/settings/members?invite=1',
  },
  settings: [
    // "Current site" pulls per-active-site config out of the
    // generic Workspace section so it reads as "what affects THIS
    // site" rather than blending in with workspace-scoped settings
    // (members, billing, etc.). The audit-config page reads the
    // active site via `useActiveSiteUuid()` — switching sites in
    // the chrome's switcher rebinds it without a URL change.
    {
      header: 'Current site',
      items: [
        { label: 'Pages', href: '/settings/pages', icon: File },
        { label: 'Audits', href: '/settings/sites', icon: Browsers },
      ],
    },
    {
      header: 'Account',
      items: [
        { label: 'Profile', href: '/settings/profile', icon: User },
        { label: 'Security', href: '/settings/security', icon: ShieldCheck },
      ],
    },
    {
      header: 'Workspace',
      items: [
        { label: 'General', href: '/settings/workspace', icon: Buildings },
        { label: 'Preferences', href: '/settings/preferences', icon: Sliders },
        { label: 'Members', href: '/settings/members', icon: Users },
        { label: 'Teams', href: '/settings/teams', icon: UsersThree },
        { label: 'Billing', href: '/settings/billing', icon: CreditCard },
        { label: 'Budgets', href: '/settings/budgets', icon: Wallet },
      ],
    },
    {
      header: 'Developer',
      items: [
        { label: 'API keys', href: '/settings/keys', icon: Key },
        { label: 'Webhooks', href: '/settings/webhooks', icon: WebhooksLogo },
        { label: 'Deploys', href: '/settings/deploys', icon: RocketLaunch },
        { label: 'Logs', href: '/settings/logs', icon: ListBullets },
      ],
    },
    {
      header: 'Danger',
      items: [{ label: 'Delete workspace', href: '/settings/danger', icon: Warning }],
    },
  ],
};

/**
 * The primary-nav entry that owns `pathname`, when that entry has
 * child pages. Drives the SidebarSubmenu panel.
 *
 * Prefix-matched, so every URL under `/traffic` — including leaves the
 * topology doesn't list, like `/traffic/session/sess_abc` — resolves to
 * the Traffic entry and keeps its panel pinned for the whole section.
 * Entries without children return `null`: there is nothing to show.
 */
export function findNavSection(pathname: string): NavEntry | null {
  const candidates: NavEntry[] = [
    ...topology.primary.flatMap((group) => group.items),
    // `tail` too — Admin is the one tail entry with children, and
    // without this its section panel would never open (the route stayed
    // URL-only after phase 4 shipped). Entries without children are
    // skipped below, so Settings/Help are unaffected.
    ...topology.tail,
  ];
  for (const entry of candidates) {
    if (!entry.children || entry.children.length === 0) continue;
    if (pathname === entry.href || pathname.startsWith(`${entry.href}/`)) return entry;
  }
  return null;
}

/**
 * Org-pill fallback brand mark — the Clickweave mark, shared with
 * `apps/dashboard` and `apps/www`. Used when an org has no custom
 * logo (the common case).
 */
export const OrgMarkFallback = Logo;
